import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardHeader, Chip, Alert,
  CircularProgress, TextField, Button,
} from '@mui/material';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';
import PsychologyHelpDialog from './PsychologyHelpDialog';
import { useQuery, useQueryClient } from 'react-query';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
  Tooltip as RTooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import { psychologyAPI } from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import HalfCircleGauge, { alertLevel } from './HalfCircleGauge';

const fmt = (v, d = 2) => (v == null ? '-' : Number(v).toFixed(d));

const fmtTime = (sec) => {
  if (!sec) return '-';
  return new Date(sec * 1000).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
};

// History helpers — find the closest snapshot to (now − offsetSec) for "hôm qua" / "tuần trước".
function pickClosestBefore(history, refUnixSec) {
  if (!Array.isArray(history) || history.length === 0) return null;
  // history is ASC by event_time; find the latest snapshot with event_time <= refUnixSec
  let chosen = null;
  for (const h of history) {
    if (h.event_time <= refUnixSec) chosen = h;
    else break;
  }
  return chosen;
}

const GAUGES = [
  { key: 'revenge',       title: 'Trả thù',           thrKey: 'thr_revenge',        inverted: false, color: '#ef4444' },
  { key: 'greed',         title: 'Tham lam',          thrKey: 'thr_greed',          inverted: false, color: '#f97316' },
  { key: 'fear',          title: 'Sợ hãi',            thrKey: 'thr_fear',           inverted: false, color: '#3b82f6' },
  { key: 'cluster_risk',  title: 'Rủi ro cụm lệnh',   thrKey: 'thr_cluster_risk',   inverted: false, color: '#a855f7' },
  { key: 'discipline',    title: 'Kỷ luật',           thrKey: 'thr_discipline',     inverted: true,  color: '#22c55e' },
  { key: 'dca_stack',     title: 'DCA chồng lệnh',    thrKey: 'thr_dca_stack',      inverted: false, color: '#ec4899' },
];

// ─── Mini area chart cho 1 cảm xúc — small-multiples style ────────────────────
// Mỗi card hiển thị 1 score 0–100 theo thời gian, với đường ngưỡng cảnh báo
// và badge giá trị hiện tại. Nhỏ gọn để xếp grid 3×2.
function EmotionAreaCard({ data, gauge, threshold }) {
  const gradId = `gp-${gauge.key}`;
  const last = data.length > 0 ? Number(data[data.length - 1][gauge.key]) || 0 : null;
  const thrLine = threshold > 0 ? threshold : null;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.08)' }}>
      <Box sx={{
        py: 0.75, px: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        background: `linear-gradient(90deg, ${gauge.color}22 0%, transparent 100%)`,
      }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: gauge.color }}>
          {gauge.title}
        </Typography>
        {last != null && (
          <Box sx={{
            px: 1, py: 0.2, borderRadius: 0.5,
            bgcolor: `${gauge.color}33`, color: gauge.color,
            fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace',
          }}>
            {last.toFixed(0)}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 1, pt: 1.5 }}>
        <ResponsiveContainer width="100%" height={120}>
          <AreaChart
            data={data}
            margin={{ top: 4, right: 4, left: 0, bottom: 0 }}
            syncId="psychology-emotions"
          >
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={gauge.color} stopOpacity={0.55} />
                <stop offset="100%" stopColor={gauge.color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="t" type="number" domain={['dataMin', 'dataMax']}
              tickFormatter={(t) => {
                const d = new Date(t);
                return `${d.getDate()}/${d.getMonth() + 1}`;
              }}
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false} tickLine={false}
              minTickGap={20}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 50, 100]}
              tick={{ fill: '#64748b', fontSize: 9 }}
              axisLine={false} tickLine={false}
              width={22}
            />
            {/* Đường ngưỡng cảnh báo từ EA — báo cho mắt biết "qua đây = nguy hiểm" */}
            {thrLine != null && (
              <ReferenceLine
                y={thrLine}
                stroke={gauge.inverted ? '#22c55e88' : '#f8717188'}
                strokeDasharray="3 3"
                strokeWidth={1}
              />
            )}
            <RTooltip
              contentStyle={{
                background: '#1e293b', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 6, fontSize: '0.72rem', padding: '4px 8px',
              }}
              labelFormatter={(t) => new Date(t).toLocaleString('vi-VN', {
                timeZone: 'Asia/Ho_Chi_Minh',
                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
              })}
              formatter={(v) => [Number(v).toFixed(1), gauge.title]}
            />
            <Area
              type="monotone"
              dataKey={gauge.key}
              stroke={gauge.color}
              strokeWidth={1.5}
              fill={`url(#${gradId})`}
              dot={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}

// ─── Date range presets (Asia/Ho_Chi_Minh local time) ────────────────────────
// Mỗi preset trả về { from, to } dạng Date hoặc null = không giới hạn.
const PERIODS = [
  { key: 'all',          label: 'Tất cả' },
  { key: 'today',        label: 'Hôm nay' },
  { key: 'yesterday',    label: 'Hôm qua' },
  { key: 'this_week',    label: 'Tuần này' },
  { key: 'last_week',    label: 'Tuần trước' },
  { key: 'this_month',   label: 'Tháng này' },
  { key: 'last_month',   label: 'Tháng trước' },
  { key: 'last_7_days',  label: '7 ngày' },
  { key: 'last_30_days', label: '30 ngày' },
  { key: 'last_90_days', label: '90 ngày' },
  { key: 'custom',       label: 'Tùy chỉnh' },
];

function periodToRange(key, customFrom, customTo) {
  if (key === 'all') return { from: null, to: null };
  if (key === 'custom') {
    return {
      from: customFrom ? new Date(customFrom + 'T00:00:00') : null,
      to:   customTo   ? new Date(customTo   + 'T23:59:59.999') : null,
    };
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfToday = new Date(today);
  const endOfToday   = new Date(today.getTime() + 86400000 - 1);
  const startOfWeek  = (() => {
    // Monday-start week
    const d = new Date(today);
    const dow = d.getDay() === 0 ? 6 : d.getDay() - 1;
    d.setDate(d.getDate() - dow);
    return d;
  })();
  switch (key) {
    case 'today':       return { from: startOfToday, to: endOfToday };
    case 'yesterday':   return { from: new Date(today.getTime() - 86400000), to: new Date(today.getTime() - 1) };
    case 'this_week':   return { from: startOfWeek, to: endOfToday };
    case 'last_week': {
      const lwEnd = new Date(startOfWeek.getTime() - 1);
      const lwStart = new Date(startOfWeek.getTime() - 7 * 86400000);
      return { from: lwStart, to: lwEnd };
    }
    case 'this_month':  return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: endOfToday };
    case 'last_month': {
      const lmStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lmEnd   = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, -1);
      return { from: lmStart, to: lmEnd };
    }
    case 'last_7_days':  return { from: new Date(today.getTime() - 6 * 86400000), to: endOfToday };
    case 'last_30_days': return { from: new Date(today.getTime() - 29 * 86400000), to: endOfToday };
    case 'last_90_days': return { from: new Date(today.getTime() - 89 * 86400000), to: endOfToday };
    default: return { from: null, to: null };
  }
}

const toUnixSec = (d) => d ? Math.floor(d.getTime() / 1000) : null;
const dateInputVal = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ─── Section card (matches MasterPerformance look-and-feel) ───────────────────
function SectionCard({ title, children, color = '#3b82f6' }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.08)' }}>
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: 'subtitle2', fontWeight: 700, fontSize: '0.8rem', letterSpacing: 0.5, textTransform: 'uppercase' }}
        sx={{
          py: 1, px: 1.5,
          background: `linear-gradient(90deg, ${color}22 0%, transparent 100%)`,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      />
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {children}
      </CardContent>
    </Card>
  );
}

// ─── Single-account psychology panel ──────────────────────────────────────────
export default function PsychologyPanel({ accountId }) {
  const queryClient = useQueryClient();
  const { lastMessage } = useWebSocket();

  // Period filter state — persist across reloads via localStorage (per-account
  // would be overkill; one global key is enough for typical usage).
  const [periodKey, setPeriodKey] = useState(() => {
    try { return localStorage.getItem('psychology_period') || 'all'; }
    catch { return 'all'; }
  });
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => {
    try { localStorage.setItem('psychology_period', periodKey); } catch {}
  }, [periodKey]);

  const dateRange = useMemo(
    () => periodToRange(periodKey, customFrom, customTo),
    [periodKey, customFrom, customTo]
  );

  const snapshotQ = useQuery(
    ['psychology', accountId],
    () => psychologyAPI.getOne(accountId),
    { enabled: !!accountId, refetchInterval: 15000, retry: false }
  );

  // Always fetch full history (limit 1000) — yesterday/lastweek footers + period
  // filter both consume from this single source. Filtering is done client-side
  // so changing the date range doesn't refetch.
  const historyQ = useQuery(
    ['psychology-history', accountId],
    () => psychologyAPI.getHistory(accountId, { limit: 1000 }),
    { enabled: !!accountId, refetchInterval: 60000, retry: false }
  );

  // Live refresh on WS push
  useEffect(() => {
    if (lastMessage?.type === 'mt5_psychology' && lastMessage.account_id === accountId) {
      queryClient.invalidateQueries({ queryKey: ['psychology', accountId] });
      queryClient.invalidateQueries({ queryKey: ['psychology-history', accountId] });
    }
  }, [lastMessage, accountId, queryClient]);

  const snap = snapshotQ.data?.data?.data;
  const history = historyQ.data?.data?.data || [];

  // Apply date filter client-side. Empty range = full history.
  const filteredHistory = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return history;
    const fromSec = toUnixSec(dateRange.from);
    const toSec   = toUnixSec(dateRange.to);
    return history.filter(h => {
      if (fromSec != null && h.event_time < fromSec) return false;
      if (toSec   != null && h.event_time > toSec)   return false;
      return true;
    });
  }, [history, dateRange]);

  // Aggregate min/avg/max per score in the filtered period.
  const periodStats = useMemo(() => {
    if (!filteredHistory.length) return null;
    const stats = {};
    for (const g of GAUGES) {
      const vals = filteredHistory.map(h => Number(h[g.key]) || 0);
      const sum = vals.reduce((a, b) => a + b, 0);
      stats[g.key] = {
        min: Math.min(...vals),
        max: Math.max(...vals),
        avg: sum / vals.length,
        count: vals.length,
      };
    }
    return stats;
  }, [filteredHistory]);

  const yesterdayMap = useMemo(() => {
    const out = {};
    if (!history.length) return out;
    const now = Math.floor(Date.now() / 1000);
    const yest = pickClosestBefore(history, now - 86400);
    const week = pickClosestBefore(history, now - 7 * 86400);
    for (const g of GAUGES) {
      out[g.key] = { yesterday: yest ? yest[g.key] : null, lastWeek: week ? week[g.key] : null };
    }
    return out;
  }, [history]);

  const periodLabel = PERIODS.find(p => p.key === periodKey)?.label || periodKey;
  const isFiltered = !!(dateRange.from || dateRange.to);

  if (snapshotQ.isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  }

  if (snapshotQ.isError || !snap) {
    return (
      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
        Chưa có dữ liệu cảm xúc. Hãy attach EA <strong>Trader_Spychology_Monitor</strong> lên chart tài khoản
        master và bật <strong>InpEnableJsonExport = true</strong> để bắt đầu.
      </Alert>
    );
  }

  if (snap.error_msg) {
    return <Alert severity="warning" sx={{ fontSize: '0.85rem', mb: 2 }}>{snap.error_msg}</Alert>;
  }

  if (!snap.base_enough) {
    return (
      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
        Chưa đủ dữ liệu baseline (cần ≥ 10 lệnh đã đóng). Hiện có {snap.closed_count || 0} lệnh.
      </Alert>
    );
  }

  // ── Gauges row ──────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* Update meta */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Chip
          size="small" label={`Symbol: ${snap.symbol || '-'}`}
          sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#3b82f622', color: '#60a5fa' }}
        />
        <Chip
          size="small" label={`${snap.closed_count || 0} lệnh đã đóng`}
          sx={{ height: 22, fontSize: '0.7rem' }}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Cập nhật: {fmtTime(snap.ea_updated_at)}
        </Typography>
      </Box>

      {/* Date range filter — quick presets + custom from/to */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2, alignItems: 'center' }}>
        {PERIODS.map(p => (
          <Box
            key={p.key}
            onClick={() => setPeriodKey(p.key)}
            sx={{
              px: 1.25, py: 0.4,
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.72rem',
              fontWeight: 600,
              border: '1px solid',
              transition: 'all 0.15s',
              borderColor: periodKey === p.key ? '#8b5cf6' : 'rgba(255,255,255,0.12)',
              bgcolor: periodKey === p.key ? '#8b5cf620' : 'transparent',
              color: periodKey === p.key ? '#a78bfa' : 'text.secondary',
              '&:hover': { borderColor: '#8b5cf680', color: '#c4b5fd' },
            }}
          >
            {p.label}
          </Box>
        ))}
        {periodKey === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1, ml: 0.5 }}>
            <TextField
              type="date" size="small" label="Từ ngày"
              value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& input': { fontSize: '0.78rem', py: 0.5 }, minWidth: 150 }}
            />
            <TextField
              type="date" size="small" label="Đến ngày"
              value={customTo} onChange={(e) => setCustomTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& input': { fontSize: '0.78rem', py: 0.5 }, minWidth: 150 }}
            />
          </Box>
        )}
        {isFiltered && (
          <Typography variant="caption" sx={{ color: 'text.disabled', ml: 'auto' }}>
            {filteredHistory.length} sự kiện trong kỳ
            {dateRange.from && ` · từ ${dateInputVal(dateRange.from)}`}
            {dateRange.to   && ` · đến ${dateInputVal(dateRange.to)}`}
          </Typography>
        )}
      </Box>

      <Grid container spacing={2}>
        {GAUGES.map((g) => {
          const liveVal = snap[`live_${g.key}`];
          const closedVal = snap[`closed_${g.key}`];
          // For live exposure the live score is the "current" feeling; fall back to closed.
          const showVal = (snap.cur_open_count > 0) ? liveVal : closedVal;
          const thr = snap[g.thrKey];
          const lvl = alertLevel(showVal, thr, g.inverted);
          const yh = yesterdayMap[g.key] || {};
          return (
            <Grid item xs={12} sm={6} md={4} key={g.key}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'rgba(255,255,255,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <HalfCircleGauge
                    value={showVal}
                    title={g.title}
                    levelLabel={lvl.label}
                    levelColor={lvl.color}
                    yesterday={yh.yesterday}
                    lastWeek={yh.lastWeek}
                    reverse={g.inverted}
                    subtitle={`Ngưỡng cảnh báo: ${fmt(thr, 0)}`}
                  />
                  {snap.cur_open_count > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'space-between' }}>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>Lệnh đã đóng</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
                        {fmt(closedVal, 0)}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Current cluster panel */}
      <Box sx={{ mt: 3 }}>
        <SectionCard title="Cụm lệnh hiện tại" color="#06b6d4">
          {snap.cur_open_count === 0 ? (
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              Không có vị thế đang mở.
            </Typography>
          ) : (
            <Grid container spacing={1.5}>
              <ClusterStat label="Loại" value={snap.cur_cluster_type === 'Multi' ? `Đa lệnh (${snap.cur_open_count})` : snap.cur_cluster_type === 'Single' ? 'Lệnh đơn' : '—'} />
              <ClusterStat label="Hướng" value={dirText(snap.cur_dir_type)} />
              <ClusterStat label="Số lệnh" value={snap.cur_open_count} />
              <ClusterStat label="Volume" value={`${fmt(snap.cur_volume, 2)} lot`} />
              <ClusterStat
                label="PnL live"
                value={`$${fmt(snap.cur_pnl_live, 2)}`}
                color={snap.cur_pnl_live >= 0 ? '#4ade80' : '#f87171'}
              />
              <ClusterStat label="Thời gian" value={`${fmt(snap.cur_duration_minutes, 0)} phút`} />
              <ClusterStat label="Cùng hướng" value={snap.cur_same_dir_count} />
              <ClusterStat label="DCA Score" value={fmt(snap.cur_live_dca_score, 1)} color={snap.cur_live_is_dca ? '#f87171' : '#94a3b8'} />
              <ClusterStat label="Step TB" value={fmt(snap.cur_live_avg_price_step, 5)} />
              <ClusterStat label="Khoảng TB" value={`${fmt(snap.cur_live_avg_time_step, 1)} phút`} />
            </Grid>
          )}
        </SectionCard>
      </Box>

      {/* Period stats — only when filtered to a specific window */}
      {isFiltered && periodStats && (
        <Box sx={{ mt: 3 }}>
          <SectionCard title={`Thống kê kỳ — ${periodLabel}`} color="#a855f7">
            <Grid container spacing={1.5}>
              {GAUGES.map(g => {
                const s = periodStats[g.key];
                return (
                  <Grid item xs={6} sm={4} md={2} key={g.key}>
                    <Box sx={{ p: 1, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 1 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                        {g.title}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>TB</Typography>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary' }}>
                          {fmt(s.avg, 1)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>Min</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#4ade80' }}>{fmt(s.min, 1)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>Max</Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: '#f87171' }}>{fmt(s.max, 1)}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </SectionCard>
        </Box>
      )}

      {/* History area charts — small multiples, 1 cảm xúc / 1 card */}
      {filteredHistory.length >= 2 ? (
        <Box sx={{ mt: 3 }}>
          <SectionCard title={`Diễn biến cảm xúc theo thời gian${isFiltered ? ` — ${periodLabel}` : ''}`} color="#8b5cf6">
            <Grid container spacing={1.5}>
              {GAUGES.map(g => (
                <Grid item xs={12} sm={6} md={4} key={g.key}>
                  <EmotionAreaCard
                    data={filteredHistory.map(h => ({ ...h, t: h.event_time * 1000 }))}
                    gauge={g}
                    threshold={snap[g.thrKey]}
                  />
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        </Box>
      ) : isFiltered && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
            Không có sự kiện nào trong kỳ "{periodLabel}". Hãy chọn khoảng rộng hơn hoặc "Tất cả".
          </Alert>
        </Box>
      )}

      {/* Baselines + thresholds (compact reference) */}
      <Box sx={{ mt: 3 }}>
        <SectionCard
          color="#6366f1"
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>Tham chiếu</span>
              <Button
                size="small" variant="text" startIcon={<HelpOutlineIcon fontSize="small" />}
                onClick={() => setHelpOpen(true)}
                sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#a5b4fc',
                      '&:hover': { bgcolor: '#6366f120' } }}
              >
                Logic tính & cách đọc
              </Button>
            </Box>
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                ADR (Average Daily Range)
              </Typography>
              {/* Quét = high - low (gồm wick) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', minWidth: 75 }}>Biên độ quét</Typography>
                <RefStat label="5 ngày"  value={fmt(snap.base_adr_short, 1)} />
                <RefStat label="10 ngày" value={fmt(snap.base_adr_mid, 1)} />
                <RefStat label="20 ngày" value={fmt(snap.base_adr_long, 1)} />
              </Box>
              {/* Chạy = |close - open| (chỉ thân nến) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', minWidth: 75 }}>Biên độ chạy</Typography>
                <RefStat label="5 ngày"  value={fmt(snap.base_adr_short_run, 1)} />
                <RefStat label="10 ngày" value={fmt(snap.base_adr_mid_run, 1)} />
                <RefStat label="20 ngày" value={fmt(snap.base_adr_long_run, 1)} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Ngưỡng cảnh báo (từ EA)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <RefStat label="Trả thù" value={fmt(snap.thr_revenge, 0)} />
                <RefStat label="Tham lam" value={fmt(snap.thr_greed, 0)} />
                <RefStat label="Sợ hãi"  value={fmt(snap.thr_fear, 0)} />
                <RefStat label="Cụm"     value={fmt(snap.thr_cluster_risk, 0)} />
                <RefStat label="Kỷ luật" value={fmt(snap.thr_discipline, 0)} />
                <RefStat label="DCA"     value={fmt(snap.thr_dca_stack, 0)} />
              </Box>
            </Grid>
          </Grid>
        </SectionCard>
      </Box>

      {/* Help dialog — opened from "Tham chiếu" header button */}
      <PsychologyHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
}

function ClusterStat({ label, value, color }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Box sx={{ p: 0.5 }}>
        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: color || 'text.primary' }}>
          {value}
        </Typography>
      </Box>
    </Grid>
  );
}

function RefStat({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>{value}</Typography>
    </Box>
  );
}

function dirText(d) {
  if (d === 'Buy') return 'Chỉ Buy';
  if (d === 'Sell') return 'Chỉ Sell';
  if (d === 'MixedHedge') return 'Hỗn hợp Hedge';
  return '—';
}
